import { defineStore } from 'pinia';
import { basculerDemo } from '@/lib/modeDemo';
import { CHAPITRES, chapitreParId, type Chapitre, type Etape } from '@/components/decouverte/chapitres';
import type { PersonaId } from '@/demo/etat';

/**
 * MODE DÉCOUVERTE : le mode démo (données fictives, servies par le navigateur)
 * et la visite guidée qui s'y promène — le même couple que dans Plans
 * d'actions, pour que les outils Altonéo s'apprennent de la même façon.
 *
 * Ce store pilote :
 *  (a) l'interrupteur lu par le client d'API (`lib/modeDemo`) ;
 *  (b) le rôle ENDOSSÉ dans la démo (collaboratrice, manager, RH, admin) ;
 *  (c) l'état de la visite : chapitre, étape, progression.
 *
 * Persistance : localStorage `igd.decouverte`. Un rechargement en pleine démo
 * reste en démo, sur le même rôle, avec les chapitres déjà terminés.
 */

interface Persiste {
  actif: boolean;
  persona: PersonaId;
  termines: string[];
}

interface State {
  actif: boolean;
  persona: PersonaId;
  chapitreId: string | null;
  etape: number;
  termines: string[];
  /**
   * Incrémentée à chaque changement de monde (entrée, sortie, rôle, remise à
   * zéro) : la coquille s'en sert comme clé pour REMONTER la page courante,
   * qui recharge alors ses données au lieu d'afficher celles d'avant.
   */
  generation: number;
}

const CLE = 'igd.decouverte';

export const useDecouverteStore = defineStore('decouverte', {
  state: (): State => ({
    actif: false,
    persona: 'collaborateur',
    chapitreId: null,
    etape: 0,
    termines: [],
    generation: 0,
  }),
  getters: {
    chapitre: (s): Chapitre | null => (s.chapitreId ? chapitreParId(s.chapitreId) : null),
    etapeCourante(s): Etape | null {
      const ch = s.chapitreId ? chapitreParId(s.chapitreId) : null;
      return ch?.etapes[s.etape] ?? null;
    },
    enVisite: (s): boolean => s.actif && s.chapitreId !== null,
    estTermine: (s) => (id: string): boolean => s.termines.includes(id),
    progression: (s): number => Math.round((s.termines.filter((t) => CHAPITRES.some((c) => c.id === t)).length / CHAPITRES.length) * 100),
  },
  actions: {
    /** Relit l'état persisté — AVANT le premier appel d'API (main.ts). */
    hydrater(): void {
      try {
        const brut = localStorage.getItem(CLE);
        if (brut) {
          const p = JSON.parse(brut) as Partial<Persiste>;
          this.actif = p.actif === true;
          if (p.persona && ['collaborateur', 'manager', 'rh', 'admin'].includes(p.persona)) this.persona = p.persona;
          this.termines = Array.isArray(p.termines) ? p.termines.filter((t) => typeof t === 'string') : [];
        }
      } catch {
        /* stockage indisponible : on démarre hors démo */
      }
      basculerDemo(this.actif);
      if (this.actif) void this.brancherPersona();
    },

    persister(): void {
      try {
        const p: Persiste = { actif: this.actif, persona: this.persona, termines: this.termines };
        localStorage.setItem(CLE, JSON.stringify(p));
      } catch {
        /* la progression n'est pas critique */
      }
    },

    /** Informe le serveur de démo du rôle endossé (chargé à la demande). */
    async brancherPersona(): Promise<void> {
      const { endosser } = await import('@/demo/serveur');
      endosser(this.persona);
    },

    /** Entre en démo, sur un rôle donné. L'identité est relue par l'appelant (`useModeDemo`). */
    async entrer(persona?: PersonaId): Promise<void> {
      if (persona) this.persona = persona;
      this.actif = true;
      basculerDemo(true);
      await this.brancherPersona();
      this.generation += 1;
      this.persister();
    },

    async changerPersona(persona: PersonaId): Promise<void> {
      this.persona = persona;
      await this.brancherPersona();
      this.generation += 1;
      this.persister();
    },

    quitter(): void {
      this.actif = false;
      this.chapitreId = null;
      this.etape = 0;
      basculerDemo(false);
      this.generation += 1;
      this.persister();
    },

    async reinitialiser(): Promise<void> {
      const { reinitialiserDemo } = await import('@/demo/serveur');
      reinitialiserDemo();
      await this.brancherPersona();
      this.chapitreId = null;
      this.etape = 0;
      this.generation += 1;
    },

    /* ---------------- Visite ---------------- */

    lancerChapitre(id: string): void {
      if (!chapitreParId(id)) return;
      this.chapitreId = id;
      this.etape = 0;
    },

    fermerVisite(): void {
      this.chapitreId = null;
      this.etape = 0;
    },

    suivante(): void {
      const ch = this.chapitre;
      if (!ch) return;
      if (this.etape >= ch.etapes.length - 1) {
        if (!this.termines.includes(ch.id)) this.termines.push(ch.id);
        this.persister();
        this.fermerVisite();
      } else {
        this.etape += 1;
      }
    },

    precedente(): void {
      if (this.etape > 0) this.etape -= 1;
    },

    oublierProgression(): void {
      this.termines = [];
      this.persister();
    },
  },
});
